import { isEmpty, isNumber } from "lodash-es";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { diffUntilNow } from "@/core/dates";
import { IJobRequest } from "@/data/job-requests";
import { formatMultipleLocations } from "@/data/locations";
import { Col, Flex, IFlexProps, Row } from "@/eworkitComponents";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFormats } from "@/hooks/Formats";
import { JobRequestDetailIconSection } from "@/views/JobRequests/Detail/components";
import { JobRequestExpandableDetailIconSection } from "@/views/JobRequests/Detail/components/JobRequestExpandableDetailIconSection";

interface IDCCDigestProps extends IFlexProps {
  jobRequest: IJobRequest;
}

const BASE_NUMBER_OF_LOCATIONS_SHOWN = 2;

export const DCCDigest: FC<IDCCDigestProps> = ({ jobRequest, ...rest }) => {
  const { t } = useTranslation("session");
  const codelistTranslation = useCodelistTranslation();

  const { formatDate, formatDateTime } = useFormats();
  const locationEmpty = isEmpty(jobRequest.locations);
  const rateEmpty = isEmpty(jobRequest.rate) || !jobRequest.rate.maxRate;
  const daysLeft = diffUntilNow(jobRequest.lastDayOfApplications, "days");
  const daysLeftString = daysLeft > 0 ? t("TASKS.DESIGNATED_CANDIDATE_CREATE.DAYS_LEFT", { count: daysLeft }) : "";

  return (
    <Flex direction="column" {...rest}>
      <Row>
        <Col width={{ xxs: 12, sm: 6 }}>
          <JobRequestDetailIconSection
            label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.CLIENT")}
            value={
              jobRequest.promoteClient && jobRequest.client?.name ? jobRequest.client?.name : jobRequest.origin.name
            }
            icon="company"
          />
        </Col>

        <Col width={{ xxs: 12, sm: 6 }}>
          <JobRequestDetailIconSection
            label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.PUBLISHED_DATE")}
            value={formatDate(jobRequest.firstDayOfApplications)}
            icon="date-1"
          />
        </Col>

        {jobRequest.lastDayOfApplications && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.LAST_DAY_OF_APPLICATION")}
              value={`${formatDateTime(jobRequest.lastDayOfApplications)} ${daysLeftString}`}
              icon="date"
            />
          </Col>
        )}

        <Col width={{ xxs: 12, sm: 6 }}>
          <JobRequestDetailIconSection
            label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.REMOTE")}
            value={`${jobRequest.remoteness || 0}%`}
            icon="website"
          />
        </Col>

        {!locationEmpty && jobRequest.locations?.length && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestExpandableDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.LOCATION")}
              shortValue={formatMultipleLocations(t, jobRequest.locations, BASE_NUMBER_OF_LOCATIONS_SHOWN)}
              longValue={formatMultipleLocations(t, jobRequest.locations, jobRequest.locations?.length)}
              buttonLabel={t("TASKS.DESIGNATED_CANDIDATE_CREATE.LOCATION_SHOW_MORE", {
                value: jobRequest.locations!.length - BASE_NUMBER_OF_LOCATIONS_SHOWN,
              })}
              icon="pin"
            />
          </Col>
        )}

        {!rateEmpty && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HOURLY_RATE")}
              value={`${t("TASKS.DESIGNATED_CANDIDATE_CREATE.HOURLY_RATE_UP_TO")} ${jobRequest.rate.maxRate} ${
                jobRequest.rate.currency
              }/h`}
              icon="cash_multiple"
              grow={0}
            />
          </Col>
        )}

        {jobRequest.designatedCandidate?.expectedRate && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.EXPECTED_RATE")}
              value={`${jobRequest.designatedCandidate?.expectedRate} ${jobRequest.rate.currency}/h`}
              icon="cash_multiple"
              grow={0}
            />
          </Col>
        )}

        {isNumber(jobRequest.numberOfPositions) && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.NUMBER_OF_POSITIONS")}
              value={jobRequest.numberOfPositions}
              icon="users"
              grow={0}
            />
          </Col>
        )}

        {!!jobRequest.skillRole && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.SKILL_ROLE")}
              value={jobRequest.skillRole.name}
              icon="user"
              grow={0}
            />
          </Col>
        )}

        {!!jobRequest.level && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.SENIORITY_LEVEL")}
              value={codelistTranslation("skill-role-levels", jobRequest.level)}
              icon="eye"
              grow={0}
            />
          </Col>
        )}

        {!!jobRequest.businessUnitName && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.BUSINESS_UNIT")}
              value={jobRequest.businessUnitName}
              icon="company"
              grow={0}
            />
          </Col>
        )}

        {!!jobRequest.legalEntityName && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.LEGAL_ENTITY")}
              value={jobRequest.legalEntityName}
              icon="company"
              grow={0}
            />
          </Col>
        )}

        {!!jobRequest.legalEntityVatNo && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.LEGAL_ENTITY_VAT")}
              value={jobRequest.legalEntityVatNo}
              icon="vat"
              grow={0}
            />
          </Col>
        )}

        {isNumber(jobRequest.hoursPerWeek) && (
          <Col width={{ xxs: 12, sm: 6 }}>
            <JobRequestDetailIconSection
              label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HOURS_PER_WEEK")}
              value={jobRequest.hoursPerWeek}
              icon="time"
              grow={0}
            />
          </Col>
        )}
      </Row>
    </Flex>
  );
};
